import { httpsCallable, type HttpsCallableResult } from "firebase/functions";
import { fns } from "./firebase";
import { mensagemDeErro } from "./erros";

// Reexportado por conveniência: quase todo chamador de callable também traduz erro.
export { mensagemDeErro, ehDesistencia, ehFalhaDeConfiguracao } from "./erros";

/**
 * Toda escrita com regra de negócio passa por uma callable — o cliente não
 * escreve direto nas coleções operacionais, as Rules não deixam.
 */
function chamar<Entrada, Saida>(nome: string) {
  const fn = httpsCallable<Entrada, Saida>(fns, nome);
  return async (dados: Entrada): Promise<Saida> => {
    try {
      const r: HttpsCallableResult<Saida> = await fn(dados);
      return r.data;
    } catch (e) {
      throw new Error(mensagemDeErro(e));
    }
  };
}

export const aposLogin = chamar<Record<string, never>, { papeis: string[] }>("aposLogin");

export const definirPapeis = chamar<
  { uid: string; papeis: string[]; empresaIds?: string[] },
  { papeis: string[]; empresaIds: string[] }
>("definirPapeis");

export interface EntradaLancamento {
  descricao: string;
  tipo: string;
  valorCentavos: number;
  moeda?: string;
  taxaCambioMicros?: number;
  dataCompetencia: string;
  dataCaixa?: string | null;
  empresaPagadoraId?: string | null;
  empresaBeneficiadaId: string;
  contaBancariaId?: string | null;
  categoriaId: string;
  centroCustoId: string;
  projetoId?: string | null;
  contraparteId?: string | null;
  observacao?: string | null;
  origem?: string;
  enviarParaAprovacao?: boolean;
}

export const criarLancamento = chamar<
  EntradaLancamento,
  { id: string; aprovacoesNecessarias: number; possivelDuplicidade: boolean }
>("criarLancamento");

export const atualizarLancamento = chamar<
  EntradaLancamento & { id: string },
  { ok: boolean; aprovacoesInvalidadas: boolean; mudancas: string[]; aprovacoesNecessarias: number }
>("atualizarLancamento");

export const marcarPago = chamar<{ id: string; dataCaixa?: string }, { ok: boolean }>("marcarPago");

export const estornarLancamento = chamar<{ id: string; motivo?: string }, { id: string }>(
  "estornarLancamento",
);

export const definirRateio = chamar<
  {
    lancamentoId: string;
    linhas: Array<{
      empresaId: string;
      centroCustoId?: string | null;
      criterio: string;
      percentualBps: number;
    }>;
  },
  {
    linhas: Array<{ empresaId: string; percentualBps: number; valorCentavos: number }>;
    aprovacoesInvalidadas: boolean;
    aprovacoesNecessarias: number;
  }
>("definirRateio");

export const removerRateio = chamar<{ lancamentoId: string }, { ok: boolean }>("removerRateio");

export const promoverEmpresa = chamar<
  { empresaId: string; cnpj: string; projetoOrigemId?: string | null },
  { empresaId: string; cnpj: string; projetoOrigemId: string | null }
>("promoverEmpresa");

export const registrarAnexo = chamar<
  { lancamentoId: string; nome: string; url: string },
  { id: string }
>("registrarAnexo");

export const removerAnexo = chamar<{ lancamentoId: string; anexoId: string }, { ok: boolean }>(
  "removerAnexo",
);

export const registrarAprovacao = chamar<
  { lancamentoId?: string; contratoId?: string; decisao: "aprovado" | "rejeitado"; comentario?: string },
  { status: string; aprovacoesNecessarias: number }
>("registrarAprovacao");

export const criarContrato = chamar<Record<string, unknown>, { id: string }>("criarContrato");

export const atualizarContrato = chamar<
  Record<string, unknown> & { id: string },
  { ok: boolean; aprovacoesInvalidadas: boolean; mudancas: string[]; aprovacoesNecessarias: number }
>("atualizarContrato");

export const gerarParcelas = chamar<
  { id: string; ate?: string },
  { geradas: number; ate?: string; mensagem?: string }
>("gerarParcelas");

export const adicionarMovimento = chamar<
  { contaBancariaId: string; data: string; descricao: string; valorCentavos: number },
  { id: string }
>("adicionarMovimento");

export const importarExtrato = chamar<
  {
    contaBancariaId: string;
    nomeArquivo: string;
    formato?: "csv" | "ofx";
    conteudo: string;
    mapeamento?: { data: number; descricao: number; valor: number };
  },
  {
    lidos: number;
    criados: number;
    repetidos: number;
    problemas: Array<{ linha: number; motivo: string }>;
    formato: string;
  }
>("importarExtrato");

export const conciliarMovimento = chamar<
  { movimentoId: string; lancamentoId: string; justificativa?: string },
  { situacao: string; diferencaCentavos: number }
>("conciliarMovimento");

export const tratarMovimento = chamar<
  { movimentoId: string; justificativa: string },
  { ok: boolean }
>("tratarMovimento");

export const desfazerConciliacao = chamar<{ movimentoId: string }, { ok: boolean }>(
  "desfazerConciliacao",
);

export const liquidarIntercompany = chamar<{ id: string; observacao?: string }, { ok: boolean }>(
  "liquidarIntercompany",
);

export interface Sugestao {
  descricao: string;
  tipo: string;
  valorCentavos: number;
  dataCompetencia: string | null;
  empresaBeneficiadaId: string | null;
  categoriaId: string | null;
  centroCustoId: string | null;
  projetoId: string | null;
  contraparteId: string | null;
  justificativa: string;
}

export const classificarMensagem = chamar<{ texto: string }, Sugestao>("classificarMensagem");

export const semear = chamar<Record<string, never>, { criados: number; mensagem: string }>("semear");
